import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import moment from "moment";
import React from "react";

import { ModelState } from "../../../interfaces/modelState";
import dateUtil from "../../../lib/dateUtil";
import { Coverage, InsuredPerson } from "../../../providers/dlpFlow/DlpFlow.interfaces";
import { Card } from "../../card/Card";
import { Fieldset } from "../../fieldset/Fieldset";
import DatePicker from "../../InputFields/DatePicker";
import NumberInput from "../../InputFields/NumberInput";
import SliderInput from "../../InputFields/SliderInput";
import { V5, V45 } from "../../InputFields/validator/validators";
import { calculateMaxDuration } from "./calculateMaxDuration";

interface Props {
  fields: Record<string, Field<string>>;
  index: number;
  coverage: Partial<Coverage>;
  modelState: ModelState | null;
  setModelState: (onUpdatedModelState: ModelState) => void;
  onUpdateCoverageDuration: (index: number, duration: number | undefined, hasDuration: boolean) => void;
  onUpdateCoverageHasDuration: (index: number) => void;
  onUpdateCoverageEffectiveDate: (index: number, effectiveDate: string | undefined) => void;
  disabled: boolean;
  validationPropertyName: string;
  insuredPersons: Partial<InsuredPerson>[];
}

export const Duration = ({
  disabled,
  fields,
  coverage,
  index,
  insuredPersons,
  validationPropertyName,
  modelState,
  onUpdateCoverageDuration,
  onUpdateCoverageHasDuration,
  onUpdateCoverageEffectiveDate,
  setModelState,
}: Props) => {
  const minDuration = 10;
  const stepValue = 1;
  const minDate = moment().add(1, "days").toDate();
  const maxDate = moment().add(1, "days").add(3, "months").toDate();

  const updateEffectiveDate = (effectiveDate?: string) => {
    if (effectiveDate !== coverage.effectiveDate) {
      onUpdateCoverageEffectiveDate(index, effectiveDate);
    }
  };

  const updateDuration = (duration?: number) => {
    if (duration !== coverage.duration) {
      onUpdateCoverageDuration(index, duration, true);
    }
  };

  const updateCoverageHasDuration = () => {
    onUpdateCoverageHasDuration(index);
  };

  const resetModelStateUpdateDuration = (duration: number) => {
    if (modelState) {
      delete modelState[`${validationPropertyName}.Duration`];
    }
    setModelState({ ...modelState });

    if (duration !== coverage.duration) {
      onUpdateCoverageDuration(index, duration, false);
    }
  };

  if (!coverage.effectiveDate) {
    const effectiveDate = dateUtil.tomorrow().format("DD-MM-YYYY");
    onUpdateCoverageEffectiveDate(index, effectiveDate);
  }

  const maxDuration = calculateMaxDuration(insuredPersons, coverage.effectiveInsured ?? [], coverage.effectiveDate);

  const id = `coverage-${index}-duration`;
  const titleId = `${id}-title`;

  return (
    <Fieldset aria-labelledby={titleId} disabled={disabled}>
      <Card>
        <h2 id={titleId}>
          <Text field={fields.DurationTitle} />
        </h2>
        <DatePicker
          required
          disabled={disabled}
          fieldData={fields}
          name="EffectiveDate"
          id={`effectivedate_${index}`}
          inputValue={coverage.effectiveDate}
          minDate={minDate}
          maxDate={maxDate}
          modelState={modelState}
          propertyName={`${validationPropertyName}.EffectiveDate`}
          onChange={updateEffectiveDate}
        />
        <NumberInput
          required
          disabled={disabled}
          fieldData={fields}
          name="Duration"
          id={`duration_${index}`}
          validators={[V5, V45(minDuration, maxDuration)]}
          setModelState={setModelState}
          inputValue={coverage.duration}
          modelState={modelState}
          propertyName={`${validationPropertyName}.Duration`}
          onChange={updateDuration}
        />
        <SliderInput
          fieldData={fields}
          disabled={disabled}
          name="DurationSlider"
          id={`duration_slider_${index}`}
          min={minDuration}
          dynamicMinLabel={`${minDuration} jaar`}
          max={maxDuration}
          dynamicMaxLabel={`${maxDuration} jaar`}
          step={stepValue}
          inputValue={coverage.duration}
          defaultInputValue={minDuration}
          onChange={resetModelStateUpdateDuration}
          onChangeCommitted={updateCoverageHasDuration}
        />
      </Card>
    </Fieldset>
  );
};
